import classNames from "classnames";
import { FormField } from "components/types";
import { TextareaHTMLAttributes, useEffect } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import { get } from "utils/util";
import { renderErrorMessage } from "./utility";

export interface TextareaProps
  extends FormField,
    Omit<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      "name" | "defaultValue"
    > {
  error?: FieldError | undefined;
  isFlex?: boolean;
  parentContainerClassName?: string;
  containerClassName?: string;
  id?: string;
  children?: React.ReactNode;
  validationFunction?: (value: string) => boolean;
  validationErrorMessage?: string;
  textareaContainerClassName?: string;
  additionalTriggerRequired?: boolean;
  rows?: number;
  resize?: "none" | "both" | "horizontal" | "vertical";
}

export default function TextareaForm({
  label,
  placeholder,
  name,
  error,
  isFlex = false,
  requiredCondition,
  className,
  parentContainerClassName,
  containerClassName,
  children,
  defaultValue,
  isDisabled,
  validationFunction,
  validationErrorMessage,
  onBlur,
  textareaContainerClassName,
  additionalTriggerRequired = false,
  rows = 2,
  resize = "vertical",
  ...props
}: TextareaProps) {
  const {
    register,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
    control,
    trigger,
    watch,
  } = useFormContext();

  useEffect(() => {
    if (defaultValue !== undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, name, setValue]);

  useEffect(() => {
    if (additionalTriggerRequired) {
      if (requiredCondition) {
        trigger(name);
      } else {
        clearErrors(name);
        trigger(name);
      }
    }
  }, [
    requiredCondition,
    name,
    trigger,
    clearErrors,
    additionalTriggerRequired,
  ]);

  const stateValue = watch(name);

  return (
    <div
      className={classNames(
        isFlex ? "flex justify-between items-start" : "",
        "mt-5",
        containerClassName
      )}
    >
      {label && (
        <label
          className={classNames(
            "block text-sm font-semibold leading-6 text-gray-700",
            isDisabled && "!text-gray-400"
          )}
        >
          {label}
        </label>
      )}

      <div className={classNames(parentContainerClassName)}>
        <div className={classNames(textareaContainerClassName)}>
          <Controller
            name={name}
            control={control}
            rules={{ required: requiredCondition }}
            defaultValue={defaultValue as string}
            render={({ field }) => (
              <textarea
                {...props}
                id={name}
                placeholder={placeholder}
                rows={rows}
                {...register(name, {
                  required: requiredCondition,
                  validate: (value) =>
                    validationFunction && requiredCondition
                      ? validationFunction(value) || validationErrorMessage
                      : true,
                })}
                onChange={(e) => {
                  field.onChange(e);
                  if (requiredCondition && validationFunction) {
                    const isValid = validationFunction(e.target.value);
                    if (isValid) {
                      clearErrors(name);
                    } else {
                      setError(name, {
                        type: "manual",
                        message: validationErrorMessage,
                      });
                    }
                  }
                }}
                onBlur={(e) => {
                  field.onBlur();
                  onBlur && onBlur(e);
                }}
                className={classNames(
                  "block w-full text-gray-900 rounded-md border-0 px-3 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-400 focus:shadow sm:text-sm sm:leading-6",
                  `resize-${resize}`,
                  {
                    "!ring-red-600": get(errors, name),
                    "!focus:ring-red-600": get(errors, name),
                  },
                  "disabled:text-gray-400 disabled:border-gray-300 disabled:placeholder:text-gray-400",
                  requiredCondition &&
                    !stateValue &&
                    "ring-secondary-500",
                  className
                )}
                defaultValue={defaultValue as string}
                disabled={isDisabled}
              />
            )}
          />

          {children}
        </div>

        {renderErrorMessage(get(errors, name) as FieldError)}
      </div>
    </div>
  );
}